import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import getFormFieldValues from '../lib/getFormFieldValues';
import useCheckoutState, { checkoutStates } from './useCheckoutState';
import { checkoutTypes } from '../enums/checkout-types';
import useEventTracking from './useEventTracking';
import api from '../api';
import getBillingDetailsFromFields from '../lib/getBillingDetailsFromFields';
import getSessionId from '../lib/getSessionId';
import useAfterPaymentActions from './useAfterPaymentActions';
import logException from '../services/logException';
import storage from '../services/storage';
import getFormVariantValues from '../lib/getFormVariantValues';
import useCheckoutValidation from './useCheckoutValidation';
import getCheckoutPath from '../lib/getCheckoutPath';
import getSellerSlug from '../services/getSellerSlug';
import getCheckoutSlug from '../services/getCheckoutSlug';
import getTicketTypeValues from '../lib/getTicketTypeValues';
import getStripeAccount from '../lib/getStripeAccount';
import useCheckoutRouter from './useCheckoutRouter';
import useRewardful from './useRewardful';
import useTolt from './useTolt';

function useCheckoutHelpers() {
  const {
    form,
    address,
    coupon,
    dynamicPrice,
    dynamicPlanIterations,
    payWhatYouWantPrice,
    checkout,
    page,
    product,
    seller,
    visitId,
    query,
    upsell,
    isUpsell,
    isUpsellRetry,
    setIsUpsellRetry,
    isTesting,
    setFormError,
    setState,
    payment,
    setPayment,
    paymentMethod,
    stripePaymentMethodId,
    setStripePaymentMethodId,
    setSubmission,
    checkoutType,
    selectedPaymentMethod,
    paymentOption,
  } = useCheckoutState();
  const { handleAfterPaymentActions } = useAfterPaymentActions();
  const { trackPurchaseEvent, trackSubmissionEvent } = useEventTracking();
  const stripe = useStripe();
  const elements = useElements();
  const { shouldValidateCardDetails } = useCheckoutValidation();
  const router = useCheckoutRouter();
  const { rewardfulReferral } = useRewardful();
  const { toltReferral } = useTolt();

  async function validateCardDetailsByCreatingToken() {
    if (stripePaymentMethodId) {
      return;
    }

    const stripeAccount = getStripeAccount(seller);
    if (!stripeAccount) {
      return; // allow testing payments without a stripe account
    }

    const { error } = await stripe.createToken(elements.getElement(CardElement));

    if (error) {
      throw new Error(error.message);
    }
  }

  function getSubscriptionData({ recaptchaToken, stripePaymentMethodEvent }) {
    const { fields, name, email } = getFormFieldValues(
      form.fields,
      page.fields,
      stripePaymentMethodEvent,
    );

    const variants = getFormVariantValues(product.variants, form.variants);

    const url = new URL(window.location.href);

    const checkoutEventId = url.searchParams.get('checkoutEventId');

    return {
      recaptchaToken,
      ...(payment && {
        subscriptionId: payment._id,
      }),
      ...(checkoutEventId && { checkoutEventId }),
      pageId: checkout._id,
      fields,
      name,
      email,
      address,
      variants: variants,
      sellerId: seller._id,
      livemode: !isTesting,
      queryParameters: query,
      ...(dynamicPrice && { dynamicPrice }),
      ...(dynamicPlanIterations && { dynamicPlanIterations }),
      ...(isUpsell && {
        upsell: true,
        upsellRetry: isUpsellRetry,
        upsellPayment: upsell?.initialPaymentId,
        upsellSubscription: upsell?.initialSubscriptionId,
        upsellCheckout: upsell?.initialCheckoutId,
        upsellCustomer: upsell?.customerId,
      }),
      session: getSessionId(),
      visit: visitId,
      couponId: coupon?._id || null,
      ...(rewardfulReferral && { rewardfulReferral }),
      ...(toltReferral && { toltReferral }),
      selectedPaymentMethod: selectedPaymentMethod,
      paymentOption,
    };
  }

  async function createSubscription({ recaptchaToken, stripePaymentMethodEvent } = {}) {
    setState(checkoutStates.CREATING_SUBSCRIPTION);

    if (shouldValidateCardDetails) {
      await validateCardDetailsByCreatingToken();
    }

    const subscriptionData = getSubscriptionData({ recaptchaToken, stripePaymentMethodEvent });

    // TODO migrate to useMutation
    const subscription = await api.createSubscription(subscriptionData);

    if (!subscription.data) {
      if (isUpsell && !isUpsellRetry) {
        setIsUpsellRetry(true);

        throw new Error('Authentication failed, please re-enter your card details');
      }

      throw new Error(subscription.error || subscription.message || 'Subscription failed');
    }

    if (
      isUpsell &&
      !isUpsellRetry &&
      (subscription.data.stripePendingSetupIntent?.status === 'requires_action' ||
        subscription.data.stripePendingSetupIntent?.status === 'requires_payment_method')
    ) {
      setIsUpsellRetry(true);

      throw new Error('Authentication failed, please re-enter your card details');
    }

    await setPayment(subscription.data);

    if (subscription.error || subscription.message) {
      if (isUpsell && !isUpsellRetry) {
        setIsUpsellRetry(true);

        throw new Error('Authentication failed, please re-enter your card details');
      }

      throw new Error(subscription.error || 'Subscription failed');
    }

    const isIncomplete = subscription.data.status === 'incomplete';
    const hasIncompletePayment = subscription.data.stripePaymentIntentClientSecret;
    const hasPendingSetupIntent = !!subscription.data.stripePendingSetupIntent;
    const hasIncompletePaymentMethod =
      subscription.data.paymentStatus === 'requires_payment_method';

    if (isIncomplete && hasIncompletePayment) {
      await handleSubscriptionPayment(subscription.data);
    } else if (isIncomplete && hasPendingSetupIntent) {
      await handleSetupIntent(subscription.data);
    } else if (hasPendingSetupIntent && hasIncompletePaymentMethod) {
      // added this incomplete payment method case to handle the case where the user subscribes to a free subscription which means the subscription becomes active immediately but we still want to validate the payment method before continuing the user to confirmation
      // this will still create an active subscription in stripe and our database
      await handleSetupIntent(subscription.data);
    } else {
      await completePayment(subscription.data);
    }
  }

  async function createSubmission({ recaptchaToken } = {}) {
    setState(checkoutStates.CREATING_SUBMISSION);

    const { fields, name, email } = getFormFieldValues(form.fields, page.fields);

    const submissionData = {
      recaptchaToken,
      pageId: checkout._id,
      fields,
      name,
      email,
      address,
      sellerId: seller._id,
      livemode: !isTesting,
      queryParameters: query,
      session: getSessionId(),
      visit: visitId,
    };

    const submission = await api.createSubmission(checkout._id, submissionData);

    if (!submission.data) {
      throw new Error(submission.error || submission.message || 'Submission failed');
    }

    await completeSubmission(submission.data);
  }

  function getPaymentData({ recaptchaToken, stripePaymentMethodEvent }) {
    const { fields, name, email } = getFormFieldValues(
      form.fields,
      page.fields,
      stripePaymentMethodEvent,
    );

    const variants = getFormVariantValues(product.variants, form.variants);

    const tickets = getTicketTypeValues(checkout.ticketGroups, form.ticketGroups);

    const url = new URL(window.location.href);

    const checkoutEventId = url.searchParams.get('checkoutEventId');

    return {
      recaptchaToken,
      ...(payment && {
        paymentId: payment._id,
        paymentIntentId: payment.paymentIntent?.id,
      }),
      ...(checkoutEventId && { checkoutEventId }),
      pageId: checkout._id,
      fields,
      name,
      email,
      address,
      variants,
      tickets,
      sellerId: seller._id,
      livemode: !isTesting,
      queryParameters: query,
      session: getSessionId(),
      visit: visitId,
      ...(isUpsell && {
        upsell: true,
        upsellRetry: isUpsellRetry,
        upsellPayment: upsell?.initialPaymentId,
        upsellSubscription: upsell?.initialSubscriptionId,
        upsellCheckout: upsell?.initialCheckoutId,
        upsellCustomer: upsell?.customerId,
      }),
      couponId: coupon?._id || null,
      ...(payWhatYouWantPrice && { payWhatYouWantPrice }),
      ...(dynamicPrice && { dynamicPrice }),
      ...(rewardfulReferral && { rewardfulReferral }),
      ...(toltReferral && { toltReferral }),
      selectedPaymentMethod: selectedPaymentMethod,
      paymentOption: paymentOption,
    };
  }

  async function createPayment({ recaptchaToken, stripePaymentMethodEvent } = {}) {
    setState(checkoutStates.CREATING_PAYMENT);

    if (shouldValidateCardDetails) {
      await validateCardDetailsByCreatingToken();
    }

    const paymentData = getPaymentData({ recaptchaToken, stripePaymentMethodEvent });

    // TODO migrate to useMutation
    const payment = await api.createPayment(checkout._id, paymentData);

    if (!payment.data || payment.data.error) {
      if (isUpsell) {
        setIsUpsellRetry(true);
      }

      if (payment.data?._id) {
        setPayment(payment.data);
      }

      const error = payment.data?.error || payment.error || payment.message || 'Payment failed';
      throw new Error(error);
    }

    await processPayment(payment.data);
  }

  async function pollPaymentStatus(payment) {
    const pollPayment = setInterval(handlePollPaymentStatus, 5000);

    let pollCount = 0;

    async function handlePollPaymentStatus() {
      pollCount = pollCount + 1;

      const response = await api.getPaymentStatus(payment._id, getSessionId());

      const chargeStatus = response?.data?.status;
      if (chargeStatus === 'created' && pollCount < 12) {
        return;
      }

      // merge payment with response data to add status and files
      completePayment({ ...payment, ...response.data });
      clearInterval(pollPayment);
    }

    handlePollPaymentStatus();
  }

  async function pollSubscriptionStatus(subscription) {
    const pollPayment = setInterval(handlePollSubscriptionStatus, 5000);

    let pollCount = 0;

    async function handlePollSubscriptionStatus() {
      pollCount = pollCount + 1;

      const response = await api.getSubscriptionStatus(subscription._id, getSessionId());
      const subscriptionStatus = response?.data?.status;
      if (
        (subscriptionStatus === 'created' || subscriptionStatus === 'incomplete') &&
        pollCount < 12
      ) {
        return;
      }

      // merge subscription with response data to add status and files
      completePayment({ ...subscription, ...response.data });
      clearInterval(pollPayment);
    }

    handlePollSubscriptionStatus();
  }

  async function completeSubmission(submission) {
    trackSubmissionEvent(submission);

    await setSubmission(submission);

    await setState(checkoutStates.COMPLETED);

    await handleAfterPaymentActions(submission);
  }

  async function completePayment(payment) {
    trackPurchaseEvent(payment);

    await setPayment(payment);

    await setState(checkoutStates.COMPLETED);
    storage.session.removeItem('CheckoutPage.pid');

    await handleAfterPaymentActions(payment);
  }

  async function handleSubscriptionPayment(subscription) {
    const clientSecret = subscription.stripePaymentIntentClientSecret;

    const billingDetails = getBillingDetailsFromFields(subscription.fields);

    try {
      if (paymentMethod === 'multiple' || paymentMethod === 'expressCheckout') {
        const hasShipping = !!billingDetails.address && !!billingDetails.name;

        let returnUrl = window.location.href;

        // pop-up is rendered on /overlay path
        // so we need to redirect to the hosted checkout page after payment
        if (checkoutType === checkoutTypes.POPUP) {
          // use checkout origin to redirect to the correct domain
          const location = origin;

          const checkoutSlug = getCheckoutSlug(checkout);
          const sellerSlug = getSellerSlug(seller);

          const returnPath = getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.CHECKOUT);

          returnUrl = location + returnPath;
        }

        const { error } = await stripe.confirmPayment({
          elements,
          clientSecret: clientSecret,
          confirmParams: {
            return_url: returnUrl,
            payment_method_data: {
              billing_details: {
                ...(billingDetails.email && { email: billingDetails.email }),
                ...(billingDetails.name && { name: billingDetails.name }),
                ...(billingDetails.phone && { phone: billingDetails.phone }),
                ...(billingDetails.address && {
                  address: billingDetails.address,
                }),
              },
            },
            ...(hasShipping && {
              shipping: {
                name: billingDetails.name,
                address: billingDetails.address,
              },
            }),
          },
          redirect: 'if_required',
        });

        if (error) {
          throw new Error(error.message);
        }

        return pollSubscriptionStatus(subscription);
      } else {
        const paymentMethod = stripePaymentMethodId
          ? stripePaymentMethodId
          : {
              card: elements.getElement(CardElement),
              billing_details: billingDetails,
            };

        const { error } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethod,
        });

        if (error) {
          throw new Error(error.message);
        }

        return pollSubscriptionStatus(subscription);
      }
    } catch (error) {
      logException(error.message);
      setFormError(error.message);
      setState(checkoutStates.INITIAL);

      if (stripePaymentMethodId) {
        setStripePaymentMethodId(null);
      }

      await api.setSubscriptionError(error.message, subscription._id);
    }
  }

  async function handleSetupIntent(subscription) {
    const clientSecret = subscription.stripePendingSetupIntent.client_secret;

    try {
      const billingDetails = getBillingDetailsFromFields(subscription.fields);

      if (paymentMethod === 'multiple' || paymentMethod === 'expressCheckout') {
        // const hasShipping = !!billingDetails.address && !!billingDetails.name;

        let returnUrl = window.location.href;
        // pop-up is rendered on /overlay path
        // so we need to redirect to the hosted checkout page after payment
        if (checkoutType === checkoutTypes.POPUP) {
          // use checkout origin to redirect to the correct domain
          const location = origin;

          const checkoutSlug = getCheckoutSlug(checkout);
          const sellerSlug = getSellerSlug(seller);

          const returnPath = getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.CHECKOUT);

          returnUrl = location + returnPath;
        }

        const { error } = await stripe.confirmSetup({
          elements,
          clientSecret: clientSecret,
          confirmParams: {
            return_url: returnUrl,
            payment_method_data: {
              billing_details: {
                ...(billingDetails.email && { email: billingDetails.email }),
                ...(billingDetails.name && { name: billingDetails.name }),
                ...(billingDetails.phone && { phone: billingDetails.phone }),
                ...(billingDetails.address && {
                  address: billingDetails.address,
                }),
              },
            },
          },
          redirect: 'if_required',
        });

        if (error) {
          throw new Error(error.message);
        }

        return pollSubscriptionStatus(subscription);
      } else {
        const paymentMethod = stripePaymentMethodId
          ? stripePaymentMethodId
          : {
              card: elements.getElement(CardElement),
              billing_details: billingDetails,
            };

        const { error } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: paymentMethod,
        });

        if (error) {
          throw new Error(error.message);
        }

        return pollSubscriptionStatus(subscription);
      }
    } catch (error) {
      logException(error.message);
      setFormError(error.message);
      setState(checkoutStates.INITIAL);

      if (stripePaymentMethodId) {
        setStripePaymentMethodId(null);
      }

      await api.setSubscriptionError(error.message, subscription._id);
    }
  }

  // process payment
  async function processPayment(payment) {
    await setPayment(payment);

    if (payment.amount === 0 || isTesting) {
      return completePayment(payment);
    }

    if (payment.status !== 'created') {
      return completePayment(payment);
    }

    // if upsell payment is already succeeded, complete payment
    if (isUpsell && payment.paymentIntent.status === 'succeeded') {
      return pollPaymentStatus(payment);
    }

    if (
      isUpsell &&
      !isUpsellRetry &&
      (payment.paymentIntent.status === 'requires_action' ||
        payment.paymentIntent.status === 'requires_payment_method')
    ) {
      // will show payment method form
      setIsUpsellRetry(true);
      setFormError(payment.paymentIntent.last_payment_error?.message);
      await setState(checkoutStates.INITIAL);
      return;
    }

    try {
      const billingDetails = getBillingDetailsFromFields(payment.fields);

      if (paymentMethod === 'multiple' || paymentMethod === 'expressCheckout') {
        const hasShipping = !!billingDetails.address && !!billingDetails.name;

        let returnUrl = window.location.href;

        // pop-up is rendered on /overlay path
        // so we need to redirect to the hosted checkout page after payment
        if (checkoutType === checkoutTypes.POPUP) {
          // use checkout origin to redirect to the correct domain
          const location = origin;

          const checkoutSlug = getCheckoutSlug(checkout);
          const sellerSlug = getSellerSlug(seller);

          const returnPath = getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.CHECKOUT);

          returnUrl = location + returnPath;
        }

        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          clientSecret: payment.stripePaymentIntentClientSecret,
          confirmParams: {
            return_url: returnUrl,
            payment_method_data: {
              billing_details: {
                ...(billingDetails.email && { email: billingDetails.email }),
                ...(billingDetails.name && { name: billingDetails.name }),
                ...(billingDetails.phone && { phone: billingDetails.phone }),
                ...(billingDetails.address && {
                  address: billingDetails.address,
                }),
              },
            },
            ...(hasShipping && {
              shipping: {
                name: billingDetails.name,
                address: billingDetails.address,
              },
            }),
          },
          redirect: 'if_required',
        });

        if (error) {
          throw new Error(error.message);
        }

        if (
          paymentIntent.status === 'requires_action' ||
          paymentIntent.status === 'requires_payment_method'
        ) {
          setState(checkoutStates.INITIAL);
          return;
        }

        return pollPaymentStatus(payment);
      } else if (stripePaymentMethodId) {
        const { error } = await stripe.confirmCardPayment(payment.stripePaymentIntentClientSecret, {
          payment_method: stripePaymentMethodId,
        });

        if (error) {
          throw new Error(error.message);
        }

        return pollPaymentStatus(payment);
      } else if (paymentMethod === 'card') {
        const { error } = await stripe.confirmCardPayment(payment.stripePaymentIntentClientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: billingDetails,
          },
        });

        if (error) {
          throw new Error(error.message);
        }

        return pollPaymentStatus(payment);
      }
    } catch (error) {
      logException(error.message);
      setFormError(error.message);
      setState(checkoutStates.INITIAL);

      if (stripePaymentMethodId) {
        setStripePaymentMethodId(null);
      }

      if (payment?._id) {
        await api.setPaymentError(error.message, payment._id);
      }
    }
  }

  async function processPaymentIntent(paymentIntentClientSecret) {
    setState(checkoutStates.PROCESSING_PAYMENT);

    if (!stripe) return;

    if (!paymentIntentClientSecret) {
      setState(checkoutStates.PENDING);
      return;
    }

    let payment;
    try {
      const { paymentIntent, error } =
        await stripe.retrievePaymentIntent(paymentIntentClientSecret);

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === 'succeeded') {
        const { data: payment, error: chargeError } =
          await api.getChargeByClientSecret(paymentIntentClientSecret);

        if (chargeError) {
          throw new Error(chargeError);
        }

        if (!payment) {
          throw new Error('Payment not found');
        }

        completePayment(payment);
      } else if (paymentIntent.status === 'requires_payment_method') {
        logException(`Payment failed with requires_payment_method status PID ${paymentIntent.id}`);
        setState(checkoutStates.ERROR);
        storage.session.removeItem('CheckoutPage.pid');

        if (router.search.has('payment_intent')) {
          router.search.delete('payment_intent');
          router.search.delete('payment_intent_client_secret');
          router.search.delete('redirect_status');
          router.replace(router.pathname + '?' + router.search.toString());
        }
      } else {
        logException(`Payment is pending PID ${paymentIntent.id}`);
        setState(checkoutStates.PENDING);
        storage.session.removeItem('CheckoutPage.pid');
      }
    } catch (error) {
      logException(error.message);
      setFormError(error.message);
      setState(checkoutStates.ERROR);

      storage.session.removeItem('CheckoutPage.pid');

      const queryParams = new URLSearchParams(router.search);

      if (queryParams.has('payment_intent')) {
        queryParams.delete('payment_intent');
        queryParams.delete('payment_intent_client_secret');
        queryParams.delete('redirect_status');
        router.replace(router.pathname + '?' + queryParams.toString());
      }

      if (payment?._id) {
        await api.setPaymentError(error.message, payment._id);
      }
    }
  }

  return {
    createSubmission,
    createPayment,
    createSubscription,
    processPaymentIntent,
  };
}

export default useCheckoutHelpers;
